import Paragraph from '@/shared/Paragraph/ui/Paragraph';
import { BlogsListProps } from './BlogsDetailPage.types';
import * as S from './BlogsDetailPage.styles';
import Layout from '@/shared/Layout/ui/Layout';
import Subscribe from '@/shared/Subscribe/ui/Subscribe';
import URL, { blogPostPageUrl, buildRootUrl } from '@/common/app/utils/urlsUtils';
import MetaBlogsDetails from '../Meta/MetaBlogsDetails';
import { Container } from '@/common/ui/containers/Container';
import dynamic from 'next/dynamic';
import { TPublicBlogs } from '@/entities/Blogs/domain/Blogs.domain';
import { IBreadcrumbsItem } from '@/shared/Breadcrumbs/domain/Breadcrumbs.domain';
import { useEffect } from 'react';
import { IRecommendsItem } from '@/shared/FeaturedActivities/ui/featuredActivitiesPreview/FeaturedActivitiesPreview.types';
import { TPageContent } from '@/entities/Content/ui/Content.types';
import { Destination } from '@/entities/Attractions/domain/Attractions.domain';
import { GoogleAdSenseBlock } from '@/shared/GoogleAdSense/GoogleAdSenseBlock';
import { Breadcrumbs } from '@/shared/Breadcrumbs/ui/Breadcrumbs';
import { cssVars } from '@/styles/Themes/cssVars';
import { MartkdownHtml } from '@/shared/MarkdownHtml';
import { Location } from '@/shared/images/icons/Location';
import emptyImg from '@/shared/images/Images/default-avatar.webp';
import { parseBlogContent } from '@/common/app/utils/parseBlogContent';
import { TableOfContents } from './TableOfContents';
import { AuthorBlock } from './AuthorBlock';
import { NewFeaturedActivities } from '@/shared/FeaturedActivities/ui/NewFeaturedActivities';
import Link from 'next/link';

const BlogsList = dynamic<BlogsListProps>(
    () => import('./BlogsList').then((module) => module.BlogsList),
    {
        ssr: false,
    }
);

export type BlogDetailsProps = {
    pageContent: TPageContent;
    currentDestination: Destination;
    breadcrumbs: IBreadcrumbsItem[];
    blogs: TPublicBlogs;
    authorDescription: string;
    authorSlug: string;
    recommendedTrips: IRecommendsItem[];
};

const BlogsDetailPage = ({
    breadcrumbs,
    pageContent,
    currentDestination,
    blogs,
    authorDescription,
    authorSlug,
    recommendedTrips,
}: BlogDetailsProps) => {
    const image = pageContent.image
        ? `${URL.image}/blog/${pageContent.image.id}/${pageContent.image.slug}.jpg`
        : '';

    const authorImage = `${URL.image}/common/main_${authorSlug}/ts.png`;

    const item_list_id = typeof window !== 'undefined' ? window.location.pathname : '';

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    const isReady = typeof window !== 'undefined' && window?.adsbygoogle?.loaded;

    useEffect(() => {
        if (!isReady) return;
        const ads = document.getElementsByClassName('adsbygoogle').length;
        for (let i = 0; i < ads; i++) {
            try {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                (adsbygoogle = window.adsbygoogle || []).push({});
            } catch (e) {
                console.log(e);
            }
        }
    }, [isReady]);

    const blogUrl = blogPostPageUrl({ slug: pageContent.slug });
    const rootUrl = blogUrl ? buildRootUrl(blogUrl) : undefined;

    const tableOfContents = parseBlogContent(pageContent.content);

    return (
        <Layout>
            <MetaBlogsDetails pageContent={pageContent} image={image} rootUrl={rootUrl} />
            <S.MainWrapper>
                <Breadcrumbs
                    breadcrumbs={breadcrumbs}
                    backgroundColor={cssVars.bg_lightBlue_darker}
                />
                <Container>
                    <S.MainContent>
                        <S.BlogInfo>
                            <div>
                                <S.Location>
                                    <Location width="12" height="12" />
                                    {pageContent.category.name}
                                </S.Location>
                                <h1>{pageContent.title}</h1>
                            </div>
                            <S.Author>
                                <S.AuthorImage>
                                    {!!authorImage && (
                                        <S.MainImage
                                            src={authorImage}
                                            width="40px"
                                            height="40px"
                                            alt="Author photo"
                                            onError={({ currentTarget }) => {
                                                currentTarget.onerror = null;
                                                currentTarget.src = emptyImg.src;
                                            }}
                                        />
                                    )}
                                </S.AuthorImage>
                                <Link passHref href={`/blog/authors/${authorSlug}/`}>
                                    <S.AuthorText>
                                        {pageContent.author && <span>by {pageContent.author}</span>}
                                        <S.AuthorMeta>
                                            <span>{pageContent.updated_at}</span>
                                        </S.AuthorMeta>
                                    </S.AuthorText>
                                </Link>
                            </S.Author>
                        </S.BlogInfo>
                        {!!image && (
                            <S.BlogImage>
                                <picture>
                                    <source
                                        type="image/webp"
                                        srcSet={image.replace('.jpg', '.webp')}
                                    />
                                    <source type="image/jpeg" srcSet={image} />
                                    <S.MainImage
                                        src={image}
                                        width="100%"
                                        height="100%"
                                        alt="Profile photo"
                                    />
                                </picture>
                            </S.BlogImage>
                        )}
                    </S.MainContent>
                </Container>
                <S.Gradient />
            </S.MainWrapper>
            <Container data-test-id="Blogs_details">
                <S.ContentBlock>
                    <S.LeftBlock>
                        {/*<!-- TS - Blog (Below "banner" image) -->*/}
                        <GoogleAdSenseBlock slotId="7619058698" />
                        <S.BlogDescription>
                            <MartkdownHtml content={pageContent.description} />
                        </S.BlogDescription>
                        <NewFeaturedActivities
                            defaultActivities={recommendedTrips || []}
                            title={`Featured ${currentDestination.name} Activities`}
                            item_list_id={item_list_id}
                            item_list_name={`Featured ${currentDestination.name} Activities`}
                            placeholders={{}}
                            noPadding
                        />
                        {/* <FeaturedActivities
                            activities={recommends || []}
                            title={`Featured ${currentDestination.name} Activities`}
                            item_list_id={item_list_id}
                            item_list_name={`Featured ${currentDestination.name} Activities`}
                            skipSkeleton
                            noPadding
                        /> */}
                        {!!tableOfContents.length && <TableOfContents items={tableOfContents} />}
                        <S.ContentWrapper id="blog-content">
                            <Paragraph
                                className="table-layout"
                                id="ts_paragraph"
                                as="div"
                                innerHtml={pageContent && pageContent.content}
                            />
                        </S.ContentWrapper>
                        <AuthorBlock
                            authorImage={authorImage}
                            authorName={pageContent.author}
                            authorSlug={authorSlug}
                            description={authorDescription}
                        />
                    </S.LeftBlock>
                    {!!tableOfContents.length && (
                        <TableOfContents items={tableOfContents} isDesktop />
                    )}
                </S.ContentBlock>
            </Container>
            <Container>
                {/*<!-- TS - Blog (below featured widget) -->*/}
                <GoogleAdSenseBlock slotId="1165010188" />
            </Container>
            <BlogsList blogs={blogs} destinationName={currentDestination.name} />
            <Container>
                {/*<!-- TS - Blog (Above email signup widget) -->*/}
                <GoogleAdSenseBlock slotId="2011508792" />
            </Container>
            <S.WrapperSubscribe>
                <Subscribe />
            </S.WrapperSubscribe>
        </Layout>
    );
};

export { BlogsDetailPage };
